import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { catalogApi } from '../lib/api'
import { db, type DiscountKind, type Promotion } from '../lib/db'
import { money } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'
import { Sheet } from '../components/Sheet'
import { NumPad } from '../components/NumPad'

/**
 * Chương trình khuyến mãi của quán.
 *
 * Đặt ở cài đặt chứ không ở màn hình bán hàng: lập chương trình là việc chủ quán làm một
 * lần lúc sáng, còn bấm nó là việc người bán làm cả ngày. Hai nhịp khác nhau thì không
 * nằm cùng một chỗ.
 */

interface Draft {
  id?: string
  name: string
  kind: DiscountKind
  value: number
  minOrderTotal: number
  maxDiscount: number
  startsOn: string
  endsOn: string
}

const blank = (): Draft => ({
  name: '',
  kind: 'Percent',
  value: 10,
  minOrderTotal: 0,
  maxDiscount: 0,
  startsOn: '',
  endsOn: '',
})

const toDraft = (p: Promotion): Draft => ({
  id: p.id,
  name: p.name,
  kind: p.kind,
  value: p.value,
  minOrderTotal: p.minOrderTotal,
  maxDiscount: p.maxDiscount,
  startsOn: p.startsOn ?? '',
  endsOn: p.endsOn ?? '',
})

/** "Bớt 10% · hoá đơn từ 100.000đ · tới 30/09" — đọc một dòng là hiểu chương trình. */
function describe(p: Promotion): string {
  const parts = [p.kind === 'Percent' ? `Bớt ${p.value}%` : `Bớt ${money(p.value)}đ`]

  if (p.kind === 'Percent' && p.maxDiscount > 0) parts.push(`tối đa ${money(p.maxDiscount)}đ`)
  if (p.minOrderTotal > 0) parts.push(`hoá đơn từ ${money(p.minOrderTotal)}đ`)
  if (p.endsOn) parts.push(`tới ${p.endsOn.split('-').reverse().slice(0, 2).join('/')}`)
  else if (p.startsOn) parts.push(`từ ${p.startsOn.split('-').reverse().slice(0, 2).join('/')}`)

  return parts.join(' · ')
}

export function PromotionsScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const owner = isOwner(useApp((s) => s.session?.role))

  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)

  const promos = useLiveQuery(() => db.promotions.orderBy('sortOrder').toArray(), [], [])

  // Máy chủ đã chặn; chặn thêm ở đây để nhân viên không mở ra rồi bấm gì cũng 403.
  if (!owner) {
    return (
      <div className="screen">
        <AppBar title="Khuyến mãi" onBack={() => nav('/settings')} />
        <div className="body center">
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🔒
            </div>
            <p>Chỉ chủ quán lập được chương trình giảm giá.</p>
          </div>
        </div>
      </div>
    )
  }

  async function save() {
    if (!draft) return

    if (!draft.name.trim()) {
      say('Chưa đặt tên chương trình')
      return
    }
    if (draft.kind === 'Percent' && (draft.value < 1 || draft.value > 100)) {
      say('Phần trăm giảm phải từ 1 đến 100')
      return
    }
    if (draft.kind === 'Amount' && draft.value < 1) {
      say('Chưa nhập số tiền giảm')
      return
    }
    if (draft.startsOn && draft.endsOn && draft.endsOn < draft.startsOn) {
      say('Ngày kết thúc trước ngày bắt đầu')
      return
    }

    setBusy(true)
    try {
      await catalogApi.savePromotion({
        id: draft.id,
        name: draft.name.trim(),
        kind: draft.kind,
        value: draft.value,
        minOrderTotal: draft.minOrderTotal,
        maxDiscount: draft.kind === 'Percent' ? draft.maxDiscount : 0,
        startsOn: draft.startsOn || null,
        endsOn: draft.endsOn || null,
      })
      setDraft(null)
      await refreshCatalog()
      say('Đã lưu chương trình')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa lưu được')
    }
    setBusy(false)
  }

  async function remove() {
    if (!draft?.id) return
    setBusy(true)
    try {
      await catalogApi.deletePromotion(draft.id)
      setDraft(null)
      await refreshCatalog()
      say('Đã bỏ chương trình')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa bỏ được')
    }
    setBusy(false)
  }

  const patch = (p: Partial<Draft>) => setDraft((c) => (c ? { ...c, ...p } : c))

  return (
    <div className="screen">
      <AppBar
        title="Khuyến mãi"
        sub={promos.length > 0 ? `${promos.length} chương trình` : undefined}
        onBack={() => nav('/settings')}
      />

      <div className="body">
        <p className="hint">
          Lập sẵn ở đây thì lúc thu tiền người bán chỉ bấm một cái, không phải tính nhẩm và
          không phải nhớ hôm nay giảm bao nhiêu.
        </p>

        {promos.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🎁
            </div>
            <p>Chưa có chương trình nào. Chủ quán vẫn bớt thẳng tiền được ở màn hình thu tiền.</p>
          </div>
        ) : (
          <div className="list">
            {promos.map((p) => (
              <button key={p.id} type="button" className="row-btn" onClick={() => setDraft(toDraft(p))}>
                <span className="row-icon" aria-hidden="true">
                  🎁
                </span>
                <span className="row-text">
                  <b>{p.name}</b>
                  <small>{describe(p)}</small>
                </span>
                <span aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="dock">
        <button type="button" className="btn btn-brand btn-lg grow" onClick={() => setDraft(blank())}>
          + Thêm chương trình
        </button>
      </div>

      {draft && (
        <Sheet
          title={draft.id ? draft.name || 'Sửa chương trình' : 'Thêm chương trình'}
          onClose={() => setDraft(null)}
        >
          <label className="field">
            <span>Tên chương trình</span>
            <input
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="Giờ vàng buổi chiều"
              maxLength={60}
              autoFocus={!draft.id}
            />
          </label>
          <p className="note">Tên này in lên bill, nên viết đúng câu muốn khách đọc.</p>

          <div className="field">
            <span>Bớt kiểu gì</span>
            <div className="seg">
              <button
                type="button"
                aria-pressed={draft.kind === 'Percent'}
                onClick={() => patch({ kind: 'Percent', value: 10 })}
              >
                Theo phần trăm
              </button>
              <button
                type="button"
                aria-pressed={draft.kind === 'Amount'}
                onClick={() => patch({ kind: 'Amount', value: 0, maxDiscount: 0 })}
              >
                Bớt thẳng tiền
              </button>
            </div>
          </div>

          {draft.kind === 'Percent' ? (
            <>
              <div className="field">
                <span>Bớt bao nhiêu phần trăm</span>
                <div className="chip-row">
                  {[5, 10, 15, 20, 30, 50].map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="chip"
                      aria-pressed={draft.value === v}
                      onClick={() => patch({ value: v })}
                    >
                      {v}%
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span>Bớt nhiều nhất (để 0 là không chặn)</span>
                <div className="amount-readout num">
                  {draft.maxDiscount ? money(draft.maxDiscount) : 'không chặn'}
                </div>
                <NumPad
                  value={draft.maxDiscount}
                  onChange={(maxDiscount) => patch({ maxDiscount })}
                />
              </div>
            </>
          ) : (
            <div className="field">
              <span>Bớt bao nhiêu tiền</span>
              <div className="amount-readout num">{draft.value ? money(draft.value) : '0'}</div>
              <NumPad value={draft.value} onChange={(value) => patch({ value })} />
            </div>
          )}

          <div className="field">
            <span>Hoá đơn từ bao nhiêu mới được dùng (0 là không ràng buộc)</span>
            <div className="amount-readout num">
              {draft.minOrderTotal ? money(draft.minOrderTotal) : 'hoá đơn nào cũng được'}
            </div>
            <NumPad
              value={draft.minOrderTotal}
              onChange={(minOrderTotal) => patch({ minOrderTotal })}
            />
          </div>

          <div className="field-row">
            <label className="field">
              <span>Chạy từ ngày</span>
              <input
                type="date"
                value={draft.startsOn}
                onChange={(e) => patch({ startsOn: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Tới ngày</span>
              <input
                type="date"
                value={draft.endsOn}
                onChange={(e) => patch({ endsOn: e.target.value })}
              />
            </label>
          </div>
          <p className="note">Để trống cả hai là chạy mãi tới khi bạn bỏ chương trình.</p>

          <div className="sheet-actions">
            {draft.id && (
              <button type="button" className="btn btn-danger-ghost" onClick={remove} disabled={busy}>
                Bỏ chương trình
              </button>
            )}
            <button type="button" className="btn btn-brand grow" onClick={save} disabled={busy}>
              {busy ? 'Đang lưu…' : 'Lưu'}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  )
}
